"use client"

import AccessDenied from "@/components/custom-access-denied"
import { CUSTOMERS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useCustomers } from "@/hook/useCustomers"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import { CustomerData, CustomerFieldType, CustomerFormData } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { SaveOutlined } from "@ant-design/icons"
import type { FormProps } from "antd"
import { Button, Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import CustomerTable from "./CustomerTable"

const UpsertCustomer = () => {
  const [customer, setCustomer] = useState<CustomerData | null>(null)
  const [initialValues, setInitialValues] = useState<CustomerFormData | null>(null)
  const [form] = Form.useForm()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id
  const router = useRouter()

  const { loading, actions } = useCustomers()
  const { fetchCustomerByIdAndSetForm, handleUpsertCustomer } = actions
  const { hasPermission: checkPermission } = useCheckPermission()
  const [isFormReady, setIsFormReady] = useState(false);
  const getNormalizedSnapshot = useCallback(() => {
    return normalizeFormData(form.getFieldsValue(true));
  }, [form])


  const { isChange, checkChange, message, commitSnapshot, handleCancel: guardCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });

  useEffect(() => {
    if (!isEditMode) {
      setIsFormReady(true);
    }
  }, [isEditMode]);

  const requiredPermission = isEditMode ? "update" : "create"

  if (!checkPermission("member", requiredPermission)) {
    return <AccessDenied />
  }

  const onFinish: FormProps<CustomerFieldType>["onFinish"] = async values => {
    const result = await handleUpsertCustomer(values, id ? Number(id) : undefined)

    if (result.success) {
      commitSnapshot();
      if (!isEditMode) {
        // Create mode: redirect to edit mode with the new customer ID
        router.push(createPath(CUSTOMERS.EDIT.PATH, searchParams, { id: result.id }))
      }
    }
  }

  const onFinishFailed: FormProps<CustomerFieldType>["onFinishFailed"] = () => {
    message.error(t("Please check the form for errors."))
  }

  const loadData = async () => {
    const { customer, formData } = await fetchCustomerByIdAndSetForm(Number(id), form)
    if (customer && formData) {
      setCustomer(customer)
      setIsFormReady(true);
    }
  }

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  useEffect(() => {
    if (isEditMode && id) {
      loadData()
    }
  }, [id, isEditMode])

  const isButtonDisabled = isEditMode ? !isChange : false

  if (isEditMode && loading && !isFormReady) {
    return <FormSkeleton mainSections={2} sidebarCards={0} />
  }

  const crumbs = [
    { title: t("Khách hàng"), href: createPath(CUSTOMERS.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <FormSection title={t("Thông tin khách hàng")} collapsible={false}>
      <CustomerTable form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} onFormChange={checkChange} />
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <>
          <Button onClick={() => guardCancel(createPath(CUSTOMERS.LIST.PATH, searchParams))}>{t("Back")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={isButtonDisabled}
            loading={loading}
            onClick={() => form.submit()}
          >
            {isEditMode ? t("Update Customer") : t("Save")}
          </Button>
        </>
      }
    />
  )

  return (
    <FormShell
      title={isEditMode ? t("Edit Customer") : t("Add New Customer")}
      crumbs={crumbs}
      main={main}
      actionBar={actionBar}
    />
  )
}

export default UpsertCustomer
